// Shared BreakdownTable column factories — the common metric columns are defined
// once here and composed per route (each route still picks its own column set).
import type { Column } from "@/components/dashboard/breakdown-table";
import type { BreakdownRow } from "@/lib/api/metrics";
import type { Platform, Brand } from "@/lib/api/types";
import { PlatformLogo } from "@/components/ui/platform-logo";
import { RegionFlag } from "@/components/ui/region-flag";
import { compact, ratio, percent } from "@/lib/format";
import { ReachFigure, ImpressionsFigure } from "@/components/dashboard/reach-figure";
import type { ReachDim, useExactReach } from "@/lib/use-reach";

type ExactReach = ReturnType<typeof useExactReach>;

/** Currency formatter from useMoney (`m.fmt`). */
type MoneyFmt = (v: number | null | undefined, opts?: { compact?: boolean }) => string;

/** Round to at most 2 decimals for numeric exports; null/undefined → "". */
const num = (v: number | null | undefined): number | "" => (v == null ? "" : Math.round(v * 100) / 100);

export const labelCol = (header: string): Column<BreakdownRow> => ({
  key: "label", header, render: (r) => <span className="font-medium">{r.label}</span>,
  exportValue: (r) => r.label,
});
/** Label column that prefixes the row with its region flag. */
export const regionLabelCol = (header: string): Column<BreakdownRow> => ({
  key: "label", header,
  render: (r) => (
    <div className="flex items-center gap-2.5">
      <RegionFlag region={(r.key || r.label) as string} size={20} />
      <span className="font-medium">{r.label}</span>
    </div>
  ),
  exportValue: (r) => r.label,
});
/** Label column that prefixes the brand with its home-market flag. */
export const brandLabelCol = (header: string, brands: Brand[]): Column<BreakdownRow> => ({
  key: "label", header,
  render: (r) => {
    const b = brands.find((br) => br.id === r.key || br.name === r.label);
    return (
      <div className="flex items-center gap-2.5">
        {b?.home_region ? <RegionFlag region={b.home_region} size={18} /> : null}
        <span className="font-medium">{r.label}</span>
      </div>
    );
  },
  exportValue: (r) => r.label,
});
/** Label column that prefixes the row with its real platform logo. */
export const platformLabelCol = (header: string, platforms: Platform[]): Column<BreakdownRow> => ({
  key: "label", header,
  render: (r) => {
    const p = platforms.find((pl) => pl.id === r.key || pl.key === r.key || pl.name === r.label);
    return (
      <div className="flex items-center gap-2.5">
        <PlatformLogo platform={p?.key ?? r.label} accentColor={p?.accent_color} size={18} />
        <span className="font-medium">{r.label}</span>
      </div>
    );
  },
  exportValue: (r) => r.label,
});
export const spendCol = (fmt: MoneyFmt): Column<BreakdownRow> => ({
  key: "spend", header: "Uitgaven", align: "right", render: (r) => fmt(r.spend, { compact: true }),
  exportValue: (r) => fmt(r.spend),
});
export const revenueCol = (fmt: MoneyFmt): Column<BreakdownRow> => ({
  key: "revenue", header: "Omzet", align: "right", render: (r) => fmt(r.revenue, { compact: true }),
  exportValue: (r) => fmt(r.revenue),
});
/** MER — all revenue over all spend; the figure Converge labels ROAS. Shown
 *  beside ROAS so a shop whose sales the ads did not claim (prado.eu) does not
 *  read as a contradiction between its revenue and its return. */
export const merCol = (): Column<BreakdownRow> => ({
  key: "mer", header: "MER", align: "right",
  render: (r) => (r.mer != null ? `${r.mer.toFixed(2)}×` : "—"),
  exportValue: (r) => (r.mer != null ? `${r.mer.toFixed(2)}×` : "—"),
});
export const roasCol = (): Column<BreakdownRow> => ({
  key: "roas", header: "ROAS", align: "right", render: (r) => (r.roas != null ? ratio(r.roas) : "—"),
  exportValue: (r) => (r.roas != null ? ratio(r.roas) : "—"),
});
export const ordersCol = (): Column<BreakdownRow> => ({
  key: "orders", header: "Orders", align: "right", render: (r) => compact(r.orders),
  exportValue: (r) => num(r.orders),
});
/** Reach as each platform deduplicates it for the period (summed across
 *  platforms, brands or markets in the row — never across days). */
export const exactReachCol = (reach: ExactReach, dim: ReachDim, header = "Bereik"): Column<BreakdownRow> => ({
  key: "reach", header, align: "right",
  render: (r) => <ReachFigure loading={reach.loading} value={reach.get(dim, r.key)} format={(v) => compact(v.reach)} />,
  exportValue: (r) => reach.get(dim, r.key)?.reach ?? "",
});
/** Engagement ÷ impressions over the platforms that report engagement. */
export const exactErCol = (reach: ExactReach, dim: ReachDim): Column<BreakdownRow> => ({
  key: "er", header: "ER", align: "right",
  render: (r) => <ReachFigure loading={reach.loading} value={reach.get(dim, r.key)} format={(v) => percent(v.er, 2)} reachCaveatsApply={false} />,
  exportValue: (r) => { const v = reach.get(dim, r.key); return v?.er == null ? "" : Math.round(v.er * 1e6) / 1e6; },
});
export const aovCol = (fmt: MoneyFmt): Column<BreakdownRow> => ({
  key: "aov", header: "AOV", align: "right", render: (r) => fmt(r.aov),
  exportValue: (r) => fmt(r.aov),
});
export const cvrCol = (): Column<BreakdownRow> => ({
  key: "cvr", header: "CVR", align: "right", render: (r) => percent(r.conversion_rate),
  exportValue: (r) => percent(r.conversion_rate),
});
/** Impressions; Instagram's row holds Views, shown as impressions. */
export const impressionsCol = (): Column<BreakdownRow> => ({
  key: "impressions", header: "Impressies", align: "right", render: (r) => <ImpressionsFigure row={r} />,
  exportValue: (r) => num(r.impressions),
});
export const clicksCol = (): Column<BreakdownRow> => ({
  key: "clicks", header: "Kliks", align: "right", render: (r) => (r.clicks ? compact(r.clicks) : "—"),
  exportValue: (r) => num(r.clicks),
});
export const ctrCol = (): Column<BreakdownRow> => ({
  key: "ctr", header: "CTR", align: "right", render: (r) => percent(r.ctr),
  exportValue: (r) => percent(r.ctr),
});
